import React, { createContext, useCallback, useContext, useState } from 'react'
import {
  type PriceIntentCarTrialExtensionFragment,
  type ProductOfferFragment,
  type ShopSessionFragment,
} from '@/services/graphql/generated'
import { convertToDate } from '@/utils/date'
import { type TrialContract } from '../carDealership.types'
import { useEditAndConfirm } from '../useEditAndConfirm'
import { useAcceptExtension } from './useAcceptExtension'

export const MILEAGE_DATA_KEY = 'mileage'
export const SELECTED_TIER_KEY = 'tier'

const getOfferByTierLevel = (offers: Array<ProductOfferFragment>, tierLevel: string) => {
  return offers.find((offer) => offer.variant.typeOfContract === tierLevel)
}

const getSelectedOffer = (
  priceIntent: PriceIntentCarTrialExtensionFragment,
  selectedTierLevel: string,
): ProductOfferFragment => {
  const matchedSelectedOffer = getOfferByTierLevel(priceIntent.offers, selectedTierLevel)
  if (matchedSelectedOffer) {
    return matchedSelectedOffer
  }

  if (priceIntent.defaultOffer) {
    const matchedDefaultOffer = getOfferByTierLevel(priceIntent.offers, priceIntent.defaultOffer.variant.typeOfContract)
    if (matchedDefaultOffer) {
      return matchedDefaultOffer
    }
  }

  return priceIntent.offers[0]
}

type CarExtensionOffersContextType = {
  isLoading: boolean
  offers: Array<ProductOfferFragment>
  selectedOffer: ProductOfferFragment
  selectedMilage: string
  extensionActivationDate: Date
  trialTerminationDate: Date
  requirePaymentConnection: boolean
  isSigningWithExtension: boolean
  isSigningWithoutExtension: boolean
  isExtendingAfterConnectingPayment: boolean
  hasUserRejectedExtension: boolean
  onTierChange: (tierLevel: string) => void
  editExtensionOffers: (data: Record<string, unknown>) => void
  addExtensionOffer: () => void
  removeExtensionOffer: () => void
  acceptSelectedExtensionOffer: () => void
  rejectExtensionOffer: () => void
}

const CarExtensionOffersContext = createContext<CarExtensionOffersContextType | null>(null)

type CarExtensionOffersProps = {
  priceIntent: PriceIntentCarTrialExtensionFragment
  shopSession: ShopSessionFragment
  requirePaymentConnection: boolean
  trialContract: TrialContract
  children: React.ReactNode
}

export const CarExtensionOffersProvider = ({
  priceIntent,
  shopSession,
  requirePaymentConnection,
  trialContract,
  children,
}: CarExtensionOffersProps) => {
  // If payment connection is required, it means we are in the initial payment connection flow
  // Otherwise, we are in the extension flow
  const [userWantsExtension, setUserWantsExtension] = useState(() => !requirePaymentConnection)
  const [hasUserRejectedExtension, setHasUserRejectedExtension] = useState(false)

  const [tierLevel, setTierLevel] = useState<string>(() => {
    return priceIntent.defaultOffer?.variant.typeOfContract ?? priceIntent.offers[0].variant.typeOfContract
  })

  const [editExtensionOffers, isEditing] = useEditAndConfirm({
    priceIntentId: priceIntent.id,
  })

  const [acceptExtension, isAccepting] = useAcceptExtension({
    shopSession: shopSession,
    trialContractId: trialContract.id,
    requirePaymentConnection: requirePaymentConnection,
  })

  const isLoading = isEditing || isAccepting

  const offers = priceIntent.offers
  const selectedOffer = getSelectedOffer(priceIntent, tierLevel)
  const selectedMilage = String(priceIntent.data[MILEAGE_DATA_KEY])

  const extensionActivationDate = convertToDate(selectedOffer.startDate)
  if (!extensionActivationDate) {
    throw new Error(`Start date must be defined for offer  ${selectedOffer.id}`)
  }

  const trialTerminationDate = convertToDate(trialContract.terminationDate)
  if (!trialTerminationDate) {
    throw new Error(`Unable to parse terminationDate: ${trialContract.terminationDate}`)
  }

  const acceptSelectedExtensionOffer = useCallback(() => {
    acceptExtension(selectedOffer.id)
  }, [acceptExtension, selectedOffer])

  const onTierChange = useCallback(
    (tierLevel: string) => {
      const match = priceIntent.offers.find((item) => item.variant.typeOfContract === tierLevel)

      if (!match) {
        throw new Error(`Unable to find offer with tierLevel ${tierLevel}`)
      }

      setTierLevel(tierLevel)
    },
    [priceIntent],
  )

  const addExtensionOffer = useCallback(() => {
    setUserWantsExtension(true)
  }, [])

  const removeExtensionOffer = useCallback(() => {
    setUserWantsExtension(false)
  }, [])

  const rejectExtensionOffer = useCallback(() => {
    setHasUserRejectedExtension(true)
  }, [])

  const isSigningWithExtension = requirePaymentConnection && userWantsExtension
  const isSigningWithoutExtension = requirePaymentConnection && !userWantsExtension
  const isExtendingAfterConnectingPayment = !requirePaymentConnection && userWantsExtension

  const contextValue = {
    isLoading,
    offers,
    selectedOffer,
    selectedMilage,
    requirePaymentConnection,
    extensionActivationDate,
    trialTerminationDate,
    isSigningWithExtension,
    isSigningWithoutExtension,
    isExtendingAfterConnectingPayment,
    hasUserRejectedExtension,
    onTierChange,
    editExtensionOffers,
    addExtensionOffer,
    removeExtensionOffer,
    acceptSelectedExtensionOffer,
    rejectExtensionOffer,
  }

  return <CarExtensionOffersContext.Provider value={contextValue}>{children}</CarExtensionOffersContext.Provider>
}

export const useCarExtensionOffers = () => {
  const context = useContext(CarExtensionOffersContext)

  if (!context) {
    throw new Error('useCarExtensionOffers must be used within a CarExtensionOffersProvider')
  }

  return context
}
